import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewChild, Output, Input, HostBinding, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

class FsTruncateComponent {
    constructor(_zone, _cd) {
        this._zone = _zone;
        this._cd = _cd;
        this.contentEl = null;
        this.contentWrapper = null;
        this.contentChanged = new EventEmitter();
        this.lines = 1;
        this.moreVisible = false;
        this.tooltip = true;
        this.content = '';
        this.contentTruncating = true;
        this.linesClass = '';
        this.showMore = false;
        this.showLess = false;
        this.expanded = false;
        this.tooltipDisabled = true;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._listenContentChanges();
        this._listenWindowResize();
    }
    ngAfterContentInit() {
        this._zone.runOutsideAngular(() => {
            this._mutationObserver = new MutationObserver(() => {
                this.contentChanged.emit();
            });
            const config = { attributes: false, childList: true, characterData: true, subtree: true };
            this._mutationObserver.observe(this.contentEl.nativeElement, config);
        });
        // Initial calculations
        this._contentUpdated();
    }
    ngOnChanges(changes) {
        if (changes) {
            if (changes.moreVisible || changes.tooltip) {
                this._checkButtonsVisibility();
                this._checkTooltipDisabled();
            }
            if (changes.lines) {
                this.linesClass = 'lines-' + this.lines;
            }
            this._cd.detectChanges();
        }
    }
    ngOnDestroy() {
        if (this._mutationObserver) {
            this._mutationObserver.disconnect();
        }
        this._destroy$.next();
        this._destroy$.complete();
    }
    toggle() {
        const initialContentNotTruncated = !this.contentTruncating && !this.expanded;
        if (!this.moreVisible || initialContentNotTruncated) {
            return;
        }
        if (this.contentTruncating) {
            this.moreClick();
        }
        else {
            this.lessClick();
        }
    }
    moreClick() {
        this.expanded = true;
        this.contentTruncating = false;
        this.showMore = this.contentTruncating;
        this.showLess = !this.contentTruncating;
        this._checkTooltipDisabled();
    }
    lessClick() {
        this.expanded = false;
        this.contentTruncating = true;
        this.showMore = this.contentTruncating;
        this.showLess = !this.contentTruncating;
        this._checkTooltipDisabled();
    }
    _listenWindowResize() {
        fromEvent(window, 'resize')
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(() => {
            this._contentUpdated();
        });
    }
    _listenContentChanges() {
        this.contentChanged
            .pipe(debounceTime(200), takeUntil(this._destroy$))
            .subscribe(() => {
            this._contentUpdated();
            this._cd.markForCheck();
        });
    }
    _contentUpdated() {
        this.content = this.contentEl.nativeElement.innerHTML;
        this._calculateTruncated();
        this._cd.markForCheck();
    }
    _calculateTruncated() {
        this._contentTruncatingUpdate();
        this._checkTooltipDisabled();
        this._checkButtonsVisibility();
    }
    _checkButtonsVisibility() {
        this.showMore = false;
        this.showLess = false;
        if (this.moreVisible) {
            if (this.contentTruncating) {
                this.showMore = true;
                this.showLess = false;
            }
            else {
                this.showMore = false;
                this.showLess = true;
            }
        }
    }
    _checkTooltipDisabled() {
        this.tooltipDisabled = !this.contentTruncating || !this.tooltip;
    }
    _contentTruncatingUpdate() {
        const element = this.contentEl.nativeElement;
        // Sometimes because of line-height and font-size % there could could be a difference in height
        this.contentTruncating = (element.scrollHeight / element.offsetHeight) > 1;
        this._cd.markForCheck();
    }
}
FsTruncateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsTruncateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsTruncateComponent, selector: "fs-truncate", inputs: { lines: "lines", moreVisible: ["more", "moreVisible"], tooltip: "tooltip" }, outputs: { contentChanged: "contentChanged" }, host: { properties: { "class.truncated": "this.contentTruncating" } }, viewQueries: [{ propertyName: "contentEl", first: true, predicate: ["contentEl"], descendants: true, static: true }, { propertyName: "contentWrapper", first: true, predicate: ["contentWrapper"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"{ truncate: lines, truncated: contentTruncating }\" [class]=\"linesClass\">\n  <span #contentEl class=\"content\" [ngClass]=\"{ 'more-visible': moreVisible }\" [matTooltip]=\"content\" [matTooltipDisabled]=\"tooltipDisabled\" (click)=\"toggle()\">\n    <ng-content></ng-content>\n  </span>\n</div>\n<div class=\"more\" [ngClass]=\"{ 'show-more': showMore }\" (click)=\"moreClick()\">See More</div>\n<div class=\"less\" [ngClass]=\"{ 'show-less': expanded && showLess }\" (click)=\"lessClick()\">See Less</div>\n", styles: [".truncate.truncated{-webkit-box-orient:vertical;display:-webkit-box;-webkit-line-clamp:1;overflow:hidden;position:relative}.truncate.truncated.lines-2{-webkit-line-clamp:2}.truncate.truncated.lines-3{-webkit-line-clamp:3}.truncate.truncated.lines-4{-webkit-line-clamp:4}.truncate.truncated.lines-5{-webkit-line-clamp:5}.truncate.truncated .content{display:block}.truncate.truncated .content.more-visible{-webkit-user-select:auto!important;-moz-user-select:auto!important;user-select:auto!important;touch-action:auto!important;cursor:pointer}.more,.less{color:#555;font-size:85%;white-space:nowrap;cursor:pointer;padding-top:6px;display:none}.show-more,.show-less{display:inline}\n"], directives: [{ type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.MatTooltip, selector: "[matTooltip]", exportAs: ["matTooltip"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-truncate',
                    templateUrl: 'truncate.component.html',
                    styleUrls: ['truncate.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { contentEl: [{
                type: ViewChild,
                args: ['contentEl', { static: true }]
            }], contentWrapper: [{
                type: ViewChild,
                args: ['contentWrapper']
            }], contentChanged: [{
                type: Output
            }], lines: [{
                type: Input
            }], moreVisible: [{
                type: Input,
                args: ['more']
            }], tooltip: [{
                type: Input
            }], contentTruncating: [{
                type: HostBinding,
                args: ['class.truncated']
            }] } });

class FsTruncateModule {
    static forRoot() {
        return {
            ngModule: FsTruncateModule
        };
    }
}
FsTruncateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsTruncateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateModule, declarations: [FsTruncateComponent], imports: [CommonModule,
        MatTooltipModule], exports: [FsTruncateComponent] });
FsTruncateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateModule, imports: [[
            CommonModule,
            MatTooltipModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsTruncateModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatTooltipModule
                    ],
                    exports: [
                        FsTruncateComponent,
                    ],
                    declarations: [
                        FsTruncateComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FsTruncateComponent, FsTruncateModule };
